// routes/reference-picker.js — 넘김과 반환이 **같은 규칙으로** 근거를 고르게 하는 자리.
//
// 왜 한 자리인가: 두 화면은 고르는 대상이 같은데 고르는 방법이 달랐다. 넘김은 체크박스
// 목록, 반환은 `<select multiple>` 이었다. 그래서 반환에서는 「몇 개 골랐는지」도,
// 「지금 고른 것이 무엇인지」도 보이지 않았고, 실수로 Ctrl 없이 클릭하면 앞서 고른 것이
// 통째로 지워졌다. 규칙이 갈리면 언제나 한쪽이 뒤처진다.
//
// 이 파일에는 **DOM 도 Preact 도 없다.** 「필터를 바꿔도 고른 것은 남는다」,
// 「순서를 올리면 읽는 순서가 바뀐다」 같은 문장을 브라우저 없이 값으로 확인한다.

/** 검색어를 항(term)으로. 공백은 AND 다 — 좁히려고 더 적는 것이 자연스럽다. */
export function searchTerms(query) {
  return String(query ?? '')
    .toLowerCase()
    .split(/\s+/)
    .map((term) => term.trim())
    .filter((term) => term.length > 0);
}

/**
 * 한 항목이 검색어에 걸리는가.
 *
 * 찾는 대상은 **화면에 이미 보이는 글자**뿐이다(제목·부가 설명). 보이지 않는 id 나
 * 주소까지 몰래 훑으면, 왜 걸렸는지 설명할 수 없는 결과가 나온다.
 */
export function matchesQuery(haystack, query) {
  const terms = searchTerms(query);
  if (terms.length === 0) return true;
  const text = String(haystack ?? '').toLowerCase();
  return terms.every((term) => text.includes(term));
}

/** 한 항목의 검색 대상 글자. 화면이 보여 주는 것과 같은 값이다. */
export function choiceSearchText(choice) {
  return [choice?.title, choice?.meta, choice?.groupLabel].filter(Boolean).join(' ');
}

/**
 * 목록을 좁힌다. **고른 것은 여기서 손대지 않는다** — 선택은 필터 밖에 있다.
 *
 * @param {{query?: string, group?: string}} criteria `group` 이 없거나 'all' 이면 전부
 */
export function filterReferenceChoices(choices, { query = '', group = 'all' } = {}) {
  return (choices ?? []).filter((choice) => {
    if (group !== 'all' && choice.group !== group) return false;
    return matchesQuery(choiceSearchText(choice), query);
  });
}

/**
 * 고르기/풀기. **고른 순서가 그대로 남는다** — 넘김에서 그 순서는 읽는 순서다.
 * 풀었다 다시 고르면 맨 뒤로 간다(그것이 사람이 방금 한 일이다).
 */
export function toggleSelectedIds(selected, referenceId) {
  const ids = [...(selected ?? [])];
  const at = ids.indexOf(referenceId);
  if (at === -1) return [...ids, referenceId];
  ids.splice(at, 1);
  return ids;
}

/**
 * 고른 것의 순서를 한 칸 옮긴다.
 *
 * 끝에서 더 밀면 **아무 일도 일어나지 않는다** — 순환시키면 마지막 항목이 갑자기 1번이
 * 되어, 누른 사람이 자기가 무엇을 했는지 모른다.
 */
export function moveSelectedId(selected, referenceId, delta) {
  const ids = [...(selected ?? [])];
  const at = ids.indexOf(referenceId);
  if (at === -1) return ids;
  const to = at + delta;
  if (to < 0 || to >= ids.length) return ids;
  ids.splice(at, 1);
  ids.splice(to, 0, referenceId);
  return ids;
}

/**
 * 고른 것을 **목록 순서가 아니라 고른 순서대로** 돌려준다.
 * 필터로 지금 안 보이는 것도 포함한다 — 안 보인다고 안 고른 것이 아니다.
 */
export function selectedChoices(choices, selectedIds) {
  const byId = new Map((choices ?? []).map((choice) => [choice.referenceId, choice]));
  return (selectedIds ?? []).map((id, index) => ({
    position: index + 1,
    referenceId: id,
    ...(byId.get(id) ?? { title: null, meta: null, missing: true }),
  }));
}

/**
 * 목록 아래 한 줄에 적을 수(數).
 *
 * `hiddenSelected` 가 요점이다 — 필터를 좁힌 뒤 "3개 선택됨"만 보이고 목록에는 하나도
 * 없으면 사람은 선택이 사라졌다고 읽는다. 몇 개가 지금 화면 밖에 있는지 말한다.
 */
export function selectionCounts({ total, visible, selectedIds }) {
  const visibleIds = new Set((visible ?? []).map((choice) => choice.referenceId));
  const selected = selectedIds ?? [];
  const hiddenSelected = selected.filter((id) => !visibleIds.has(id)).length;
  return {
    total: total ?? 0,
    visible: (visible ?? []).length,
    selected: selected.length,
    hiddenSelected,
    filtered: (visible ?? []).length !== (total ?? 0),
  };
}

/** 실제 데이터에서만 만든 묶음 필터. 비어 있는 묶음은 만들지 않는다. */
export function groupOptions(choices, labels = {}) {
  const counts = new Map();
  for (const choice of choices ?? []) {
    if (!choice.group) continue;
    counts.set(choice.group, (counts.get(choice.group) ?? 0) + 1);
  }
  if (counts.size < 2) return [];
  return [
    { value: 'all', label: labels.all ?? '전체', count: (choices ?? []).length },
    ...[...counts.entries()].map(([value, count]) => ({
      value,
      label: labels[value] ?? value,
      count,
    })),
  ];
}

/**
 * 이 참조가 **무엇의 어느 판인가** 한 줄로.
 *
 * 제목만으로는 같은 자료의 두 참조를 구별할 수 없다. 고정된 판이 없으면 그 사실을
 * 감추지 않는다 — 「지금의 그것」과 「그때의 그것」은 다른 약속이다.
 */
export function referenceIdentity({ relationLabel = null, mode = null, pinnedVersion = null } = {}) {
  const parts = [];
  if (relationLabel) parts.push(relationLabel);
  if (mode === 'live') parts.push('판을 고정하지 않음');
  else if (mode === 'snapshot') parts.push('이 시점 고정');
  if (pinnedVersion !== null && pinnedVersion !== undefined) parts.push(`등록 판 v${pinnedVersion}`);
  return parts.join(' · ');
}
